/* eslint-disable jsx-a11y/no-noninteractive-element-to-interactive-role */
import { SwipeActions } from 'app/consumer-app/views/repairer-search/components/SliderView';
import { RepairAssignedProduct } from 'app/core/api/product-assignment/api-product-assignment.types';
import { useInitiateChat } from 'app/core/api/repairer/api-repairer';
import AdoptiveTile from 'app/shared/frames/AdoptiveTile';
import { Opt } from 'app/tools/general.types';
import {
  useObservableSend,
  useWatchAsRef,
} from 'app/tools/hooks/misc-hooks';
import { useEffect, useRef, useState } from 'react';
import { Carousel, CarouselProps } from 'react-responsive-carousel';
import { CSSProperties } from 'styled-components/macro';
import ProductCard from './ProductCard';
import InProgressProductCard from './InProgressProductCard';

const indicatorStyles: CSSProperties = {
  background: 'rgb(0 0 0 / 26%)',
  width: 8,
  height: 8,
  display: 'inline-block',
  borderRadius: '50%',
  margin: '0 8px',
};
type Props = {
  products: RepairAssignedProduct[];
  variantType?: 'wide' | 'wide-no-icons';
  displayType?: 'withTracking' | 'withoutTracking';
  sliderName?: string;
};
export default function ProductSliderContainer({
  products,
  variantType,
  displayType,
  sliderName,
}: Props): JSX.Element {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedProduct, setSelectedProduct] =
    useState<RepairAssignedProduct>();
  const currentSlideRef = useWatchAsRef(currentSlide);
  const changingRepairerEnabled = useRef(true);
  const slideIsExpanded = useRef(false);
  const listeningForVerticalSwipe = useRef(false);
  const swipeIsInProgress = useRef(false);

  /**
   * Ref holding whether the swipe is horizontal (indicating moving carousel, so
   * value is `true`) or vertical (indicating expanding / contracting slide, so
   * value is `false` as it is not moving the carousel).
   * Value is undefined if the distinction has not been made yet.
   */
  const swipeMovesCarousel = useRef<Opt<boolean>>(undefined);

  const [sendSwiped] = useObservableSend<SwipeActions>();

  const runActionOnSlide = (action: 'expand' | 'collapse') => {
    if (action === 'expand' && !slideIsExpanded.current) {
      sendSwiped('up');
      slideIsExpanded.current = true;
      return true;
    }
    if (action === 'collapse' && slideIsExpanded.current) {
      sendSwiped('down');
      slideIsExpanded.current = false;
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (!changingRepairerEnabled.current) {
      setSelectedProduct(products[currentSlideRef.current]);
      return;
    }
    if (selectedProduct) {
      const selRepairerIdx = products.indexOf(selectedProduct);
      setCurrentSlide(selRepairerIdx === -1 ? 0 : selRepairerIdx);
    }
  }, [
    currentSlideRef,
    products,
    selectedProduct,
    setSelectedProduct,
  ]);

  const { sending: initiatingChat } = useInitiateChat();
  useEffect(() => {
    changingRepairerEnabled.current = !initiatingChat;
  }, [initiatingChat]);

  const handleSliderChange = (cSlide: number) => {
    setCurrentSlide(cSlide);
    setSelectedProduct(products[cSlide]);
  };

  /**
   * Handles the mouse/touch swipe events from onSwipeMove on Carousel
   * @param event MouseEvent or TouchEvent
   * @param delta x, y distance mouse/touch has swiped, in px
   * @returns value determining if swipe movement is allowed or adjusted:
   * - `void` or `true` -> swipe goes ahead unaffected
   * - `false` -> swipe is blocked from happening
   * - `{ x, y }` -> swipe delta is adjusted to the returned x and y values
   */
  const handleSwipeMove: CarouselProps['onSwipeMove'] = (
    event,
    { x, y } = { x: NaN, y: NaN }
  ) => {
    // if selected repairer is locked (eg. if loading starting chat),
    // return false to prevent swipe from being registered.
    if (!changingRepairerEnabled.current) return false;
    // if x is not a number, delta was missing so just return (leave as is)
    // this happened once and caused an error, but seems very rare / one-off
    if (Number.isNaN(x)) return;

    // if the swipe is happening, stop it registering as a normal drag on the
    // page. On mobile this would have caused the page to try and scroll.
    event.preventDefault();

    if (swipeMovesCarousel.current !== undefined) {
      // if swipe is already recognised as vertical or horizontal, return
      // immediately with the modified swipe movement. This means vertical
      // swipes don't cause horizontal movement of the carousel.
      const rtn = {
        x: x * Number(swipeMovesCarousel.current),
        y,
      };
      return rtn;
    }

    // work out the gradient and distance of the drag movement.
    const gradient = Math.abs(y) / Math.abs(x);
    const distance = (y ** 2 + x ** 2) ** 0.5;
    const carouselIsMoving = gradient < 1; // otherwise is a vertical swipe

    // lock action to either h or v when distance is greater than 7px;
    if (distance > 7) {
      swipeMovesCarousel.current = carouselIsMoving;
    }
    // void return means that the swipe is unaffected
  };

  const handleSwiped: CarouselProps['onSwiped'] = () =>
    changingRepairerEnabled.current && swipeMovesCarousel.current;

  const handleClickItem: CarouselProps['onClickItem'] = i => {
    if (!changingRepairerEnabled.current) return false;
    if (i === currentSlide && !swipeIsInProgress.current) {
      runActionOnSlide('expand');
    }
  };

  useEffect(() => {
    if (slideIsExpanded.current) sendSwiped('up');
  }, [sendSwiped, currentSlide]);
  // const wide = (i: number) => [2, 7].includes(i);

  return (
    <Carousel
      emulateTouch
      swipeable
      showThumbs={false}
      infiniteLoop={false}
      swipeScrollTolerance={5}
      preventMovementUntilSwipeScrollTolerance
      centerMode
      centerModeEndSpacing
      centerSlidePercentage={100}
      selectedItem={currentSlide}
      onChange={handleSliderChange}
      onSwipeMove={handleSwipeMove}
      onSwiped={handleSwiped}
      onClickItem={handleClickItem}
      onSwipeStart={() => {
        listeningForVerticalSwipe.current = true;
        swipeMovesCarousel.current = undefined;
      }}
      onSwipeEnd={() => {
        listeningForVerticalSwipe.current = false;
        setTimeout(() => {
          swipeIsInProgress.current = false;
        }, 100);
      }}
      showIndicators
      showStatus={false}
      showArrows={false}
      autoPlay={false}
      className="presentation-mode"
      renderIndicator={(onClickHandler, isSelected, index, label) => {
        if (isSelected) {
          return (
            <li
              style={{ ...indicatorStyles, background: '#31AD31' }}
              aria-label={`Selected: ${label} ${index + 1}`}
              title={`Selected: ${label} ${index + 1}`}
            />
          );
        }
        return (
          <li
            style={indicatorStyles}
            onClick={onClickHandler}
            onKeyDown={onClickHandler}
            value={index}
            key={index}
            role="button"
            tabIndex={0}
            title={`${label} ${index + 1}`}
            aria-label={`${label} ${index + 1}`}
          />
        );
      }}
    >
      {products?.slice(0, 5).map(assignedProduct => (
        <AdoptiveTile key={assignedProduct.id} wide>
          {sliderName === 'inprogress' && (
            <InProgressProductCard
              variant={variantType}
              item={assignedProduct}
              displayType={displayType}
            />
          )}
          {!sliderName && (
            <ProductCard
              variant={variantType}
              item={assignedProduct}
              displayType={displayType}
            />
          )}
        </AdoptiveTile>
      ))}
    </Carousel>
  );
}
